import { Diagnostic, Geotab, StatusData } from "@fairfleet/geotab";
import AddInIds from "@/constants/AddInIds";
import {
  RESULTS_LIMIT_ADD_IN_DATA,
  RESULTS_LIMIT_DIAGNOSTIC,
  RESULTS_LIMIT_STATUS_DATA,
} from "@/constants/resultsLimits";
import { CastedAddInData } from "@/hooks/addins/useAddinData";
import { Column, Preset } from "@/models/Preset";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { toLiveDataRow } from "@/pages/live-data/models/toLiveDataRow";
import {
  CheckKey,
  isFuelLevelPercentRow,
  isFuelLevelVolumeRow,
  isOdometerRow,
  isRawOdometerRow,
  isTachoCanCRow,
  isTachoD8Row,
  matchCheckSpecificity,
  standardIdOf,
} from "@/pages/live-data/utilities/diagnosticMatchers";
import { compressData, decompressAddIns } from "@/utilities/compression";

/** Name and id of the preset a database gets when it has none. Presets are keyed by name. */
export const DEFAULT_PRESET_ID = "Fairfleet Standard";

/** How far back the fleet's readings are sampled to find out which diagnostics it reports. */
const SAMPLE_HOURS = 2;

const MAX_IDS_PER_GET = 1000;

interface DefaultPresetCheck {
  /** How strongly a reading answers the check; 0 means not at all. */
  specificity: (row: LiveDataRow) => number;
  /** Taken even when no vehicle reported it in the sample, so the column is there once it does. */
  standardId?: string;
}

function byKey(key: CheckKey): DefaultPresetCheck {
  return { specificity: row => matchCheckSpecificity(key, row), standardId: standardIdOf(key) };
}

function byPredicate(predicate: (row: LiveDataRow) => boolean): DefaultPresetCheck {
  return { specificity: row => (predicate(row) ? 1 : 0) };
}

/**
 * The measured values of the installation and service checklist (Live-Daten, Motordaten), in its
 * order. Firmware, CAN protocols and IOX devices are not readings of one diagnostic and have no
 * column.
 */
const CHECKS: DefaultPresetCheck[] = [
  byPredicate(isRawOdometerRow),
  byPredicate(isOdometerRow),
  byKey("intelligentHarness"),
  byKey("standardHarness"),
  byKey("deviceVoltage"),
  byKey("ignition"),
  byKey("vehicleSpeed"),
  byKey("fmsStandardSoftwareVersion"),
  byKey("engineSpeed"),
  byPredicate(isFuelLevelPercentRow),
  byPredicate(isFuelLevelVolumeRow),
  byKey("defLevel"),
  byKey("fuelConsumption"),
  byKey("stateOfCharge"),
  byKey("chargingState"),
  byKey("hvBatteryCurrent"),
  byKey("hvBatteryVoltage"),
  byKey("hvBatteryPower"),
  byKey("evRange"),
  byKey("cellVoltageHigh"),
  byKey("cellVoltageLow"),
  byKey("cellTemperatureHigh"),
  byKey("cellTemperatureLow"),
  byPredicate(isTachoCanCRow),
  byPredicate(isTachoD8Row),
];

/** One reported diagnostic, and how many vehicles reported it. */
export interface ReportedDiagnostic {
  row: LiveDataRow;
  vehicleCount: number;
}

/**
 * The preset columns for the reported diagnostics: per check the best match, and among equally
 * good ones the diagnostic most vehicles report — the checklist looks at one vehicle, a preset
 * serves the fleet.
 *
 * @param reported - Every diagnostic in the sample, once.
 * @param names - Names of the standard ids, for those no vehicle reported.
 */
export function pickDefaultPresetColumns(
  reported: ReportedDiagnostic[],
  names: Record<string, string>,
): Column[] {
  const taken = new Set<string>();
  const columns: Column[] = [];

  for (const check of CHECKS) {
    let best: ReportedDiagnostic | undefined;
    let bestSpecificity = 0;

    for (const candidate of reported) {
      if (taken.has(candidate.row.diagnosticId)) continue;
      const specificity = check.specificity(candidate.row);
      if (specificity === 0) continue;
      if (
        !best ||
        specificity > bestSpecificity ||
        (specificity === bestSpecificity &&
          (candidate.vehicleCount > best.vehicleCount ||
            (candidate.vehicleCount === best.vehicleCount &&
              candidate.row.diagnosticId < best.row.diagnosticId)))
      ) {
        best = candidate;
        bestSpecificity = specificity;
      }
    }

    const id = best?.row.diagnosticId ?? check.standardId;
    if (!id || taken.has(id)) continue;

    taken.add(id);
    columns.push({
      columnSequence: columns.length + 1,
      diagnostic: { id, name: best?.row.diagnosticName ?? names[id] ?? id },
    });
  }

  return columns;
}

/** Every diagnostic in the readings once, with the number of vehicles that reported it. */
export function reportedDiagnostics(
  statusData: StatusData[],
  diagnostics: Record<string, Diagnostic>,
): ReportedDiagnostic[] {
  const byDiagnostic = new Map<string, { reading: StatusData; vehicles: Set<string> }>();

  for (const reading of statusData) {
    const diagnosticId = reading.diagnostic?.id;
    if (!diagnosticId) continue;
    const entry = byDiagnostic.get(diagnosticId) ?? { reading, vehicles: new Set<string>() };
    entry.vehicles.add(reading.device?.id ?? "");
    byDiagnostic.set(diagnosticId, entry);
  }

  const lookups = { diagnostics, units: {}, sources: {} };
  return [...byDiagnostic.values()].map(({ reading, vehicles }) => ({
    row: toLiveDataRow(reading, lookups),
    vehicleCount: vehicles.size,
  }));
}

/** Assembles the default preset from what the fleet reported recently. */
export async function buildDefaultPreset(geotabClient: Geotab): Promise<Preset> {
  const fromDate = new Date(Date.now() - SAMPLE_HOURS * 60 * 60 * 1000);
  const statusData: StatusData[] = await geotabClient.get(
    "StatusData",
    { fromDate },
    RESULTS_LIMIT_STATUS_DATA,
  );

  const standardIds = CHECKS.flatMap(check => (check.standardId ? [check.standardId] : []));
  const ids = [
    ...new Set([
      ...statusData.flatMap(reading => (reading.diagnostic?.id ? [reading.diagnostic.id] : [])),
      ...standardIds,
    ]),
  ];
  const loaded: Diagnostic[] = [];
  for (let start = 0; start < ids.length; start += MAX_IDS_PER_GET) {
    const chunk = ids.slice(start, start + MAX_IDS_PER_GET);
    loaded.push(...(await geotabClient.get("Diagnostic", { ids: chunk }, RESULTS_LIMIT_DIAGNOSTIC)));
  }
  const diagnostics = Object.fromEntries(loaded.map(diagnostic => [diagnostic.id, diagnostic]));
  const names = Object.fromEntries(loaded.map(diagnostic => [diagnostic.id, diagnostic.name ?? ""]));

  return {
    id: DEFAULT_PRESET_ID,
    description: DEFAULT_PRESET_ID,
    groups: [{ id: "" }],
    columns: pickDefaultPresetColumns(reportedDiagnostics(statusData, diagnostics), names),
  } as Preset;
}

async function readPresetRecords(geotabClient: Geotab): Promise<CastedAddInData<Preset>[]> {
  const data = await geotabClient.get(
    "AddInData",
    { addInId: AddInIds.presets },
    RESULTS_LIMIT_ADD_IN_DATA,
  );
  return decompressAddIns(data) as unknown as CastedAddInData<Preset>[];
}

export interface DefaultPresetResult {
  preset: Preset;
  /** False when it could not be written and lives only in this session. */
  stored: boolean;
}

/**
 * Makes sure a database without presets gets "Fairfleet Standard".
 *
 * Read fresh right before writing, because the cached list may be old. Two users opening a monitor
 * at the same moment can both write it; afterwards each removes every copy but the one with the
 * lowest record id, so both remove the same ones and exactly one stays.
 *
 * @returns null when the database already has other presets.
 */
export async function ensureDefaultPreset(geotabClient: Geotab): Promise<DefaultPresetResult | null> {
  const existing = await readPresetRecords(geotabClient);
  const stored = existing.find(record => record.details.id === DEFAULT_PRESET_ID);
  if (stored) return { preset: stored.details, stored: true };
  if (existing.length > 0) return null;

  const preset = await buildDefaultPreset(geotabClient);

  try {
    await geotabClient.call("Add", {
      typeName: "AddInData",
      entity: { addInId: AddInIds.presets, details: compressData(preset) },
    });
  } catch {
    // Most likely a role without the right to write add-in data.
    return { preset, stored: false };
  }

  try {
    const copies = (await readPresetRecords(geotabClient))
      .filter(record => record.details.id === DEFAULT_PRESET_ID)
      .sort((a, b) => a.id.localeCompare(b.id));
    for (const copy of copies.slice(1)) {
      await geotabClient.call("Remove", {
        typeName: "AddInData",
        entity: { id: copy.id, addInId: AddInIds.presets },
      });
    }
  } catch (error) {
    console.error("Removing duplicate default presets failed:", error);
  }

  return { preset, stored: true };
}
